import PropTypes from 'prop-types';
import { Col, Row } from 'react-bootstrap';
import { ClinicalTrialAd, MediFindMoments } from '@medifind/ads';
import { LatestAdvancesAd } from '@medifind/ads';
import { WidgetContainer } from '@medifind/containers';
import { usePageChanger, useSortChanger } from '@medifind/router';
import { toSlug } from '@medifind/router';
import { CTFiltersGroup, ClinicalTrialCardV3, ClinicalTrialResultFilterList, FilterSideContainer, Grid } from '@medifind/search-results';
import { SEO } from '@medifind/seo';
import { getNullList, scrollToId } from '@medifind/utils';
import { clearTrialFilters, useFilterContext, useLocationStore } from '@medifind/zustand';
import styles from './TrialSearchShared.module.scss';

const Results = ({ condition, onBack, onStartOver, criteriaKeywords, perPage, results }) => {
  const filters = useFilterContext();
  const sortItems = [
    { display: 'Date', value: 'date' },
    { display: 'Distance', value: 'distance' },
  ];
  const location = useLocationStore();

  const projectId = condition && condition.value;
  const codingSlug = condition && toSlug(condition.display);

  const { results: resultsData, totalResults, keywords: dataKeywords, error } = results || {};

  const { sort, handleSortChange } = useSortChanger('sort', 'relevance');
  const { page, handlePageChange } = usePageChanger('page', [...filters.valueDependencies, location, sort]);

  return (
    <div className={styles['trial-search--results']}>
      <SEO noIndex={true} />
      <Row>
        <Col sm={12} lg={4} xl={4}>
          <div className="hide--md">
            <FilterSideContainer
              title="Filter your results"
              lazyLoadOptions={{ height: 600, resize: true }}
              clearFilters={clearTrialFilters}
            >
              <ClinicalTrialResultFilterList
                showTrialCondition={true}
                criteriaKeywords={criteriaKeywords || dataKeywords}
                totalResults={totalResults}
                showLocationFilter={sort === 'distance'}
              />
            </FilterSideContainer>

            <MediFindMoments
              type={'rectangle'}
              targeting={{
                position: 'mid1',
                pageType: 'clinical-trial-search',
              }}
            />

            <ClinicalTrialAd
              onClick={() => {
                handleSortChange('distance');
                scrollToId('clinical-trial-header', 0);
              }}
            />
            <LatestAdvancesAd projectId={projectId} codingSlug={codingSlug} />
          </div>
        </Col>
        <Col sm={12} lg={8} xl={8}>
          <MediFindMoments
            type={'leaderboard'}
            targeting={{
              position: 'top',
              pageType: 'clinical-trial-search',
            }}
          />
          <WidgetContainer>
            <CTFiltersGroup
              id="clinical-trial-header"
              onSortByChange={({ value }) => handleSortChange(value)}
              sortValue={sort}
              sortItems={sortItems}
              totalResults={totalResults}
              smallAt="md"
              criteriaKeywords={criteriaKeywords || dataKeywords}
              showTrialCondition
            />
            <Grid
              error={error}
              classes={{ grid: styles['trial-grid'] }}
              items={(resultsData || getNullList(perPage)).map((trial, i) => (
                <ClinicalTrialCardV3
                  // tierAlign="label-center"
                  key={i}
                  projectId={projectId}
                  codingSlug={codingSlug}
                  trial={trial}
                  id={'trials-search-' + i}
                  showDistance={location.lat && location.lon && location.fidelity <= 3}
                  smallAt={['xs', 'lg']}
                  // showScore={true}
                  // newTab={true}
                />
              ))}
              size={perPage}
              id="trials-search"
              maxColumnCount={1}
              pagination={{
                noResults: '0 Clinical Trials Found',
                page,
                totalResults,
                onPageChange: handlePageChange,
                type: 'eventWithRouterShallowChange',
                forceScroll: true,
                requireLogin: {
                  errorMessage: `Sign in or create a free MediFind account to see more experts. View, save, and contact these experts to find the care you deserve.`,
                  errorTitle: `More Expertise Awaits`,
                  afterPage: 1,
                },
              }}
              addGutters
              noBorder
              insertResult={[
                {
                  renderAfter: (i) => (i + 1) % 4 === 0,
                  render: (i) => (
                    <MediFindMoments
                      type={'rectangle'}
                      targeting={{
                        position: `mid${(i + 1) / 4 + 1}`,
                        pageType: 'clinical-trial-search',
                      }}
                    />
                  ),
                  dontShowAtEnd: true,
                },
              ]}
            />
          </WidgetContainer>
        </Col>
      </Row>
    </div>
  );
};

Results.propTypes = {
  location: PropTypes.object,
  condition: PropTypes.object,
  criteria: PropTypes.array,
  answeredQuestions: PropTypes.array,
  isHealthy: PropTypes.object,
  gender: PropTypes.object,
  age: PropTypes.object,
  recruiting: PropTypes.object,
  onBack: PropTypes.func,
  onStartOver: PropTypes.func,
  presults: PropTypes.object,
  criteriaKeywords: PropTypes.array,
  perPage: PropTypes.number,
  results: PropTypes.object,
  filters: PropTypes.object,
};

export default Results;
